export const OVERRIDE_COMMENT_KEY = 'override comment'
export const ADHERENCE_COMMENT_KEY = 'adherence comment'

export type StructuredContainerComments = {
  [OVERRIDE_COMMENT_KEY]: string
  [ADHERENCE_COMMENT_KEY]: string
}

/**
 * Parse `comments` column value.
 * - Structured JSON → { "override comment", "adherence comment" }
 * - Legacy plain text / empty / invalid → null (caller displays raw text)
 */
/** True for empty / whitespace / `{}` — not real comment content. */
export function isBlankContainerComments(raw?: string | null): boolean {
  const text = raw?.trim() ?? ''
  return !text || text === '{}'
}

export function parseContainerComments(raw?: string | null): StructuredContainerComments | null {
  if (isBlankContainerComments(raw) || raw!.trim()[0] !== '{') {
    return null
  }
  try {
    const data = JSON.parse(raw!) as Record<string, unknown>
    if (
      data == null ||
      typeof data !== 'object' ||
      Array.isArray(data) ||
      (!(OVERRIDE_COMMENT_KEY in data) && !(ADHERENCE_COMMENT_KEY in data))
    ) {
      return null
    }
    return {
      [OVERRIDE_COMMENT_KEY]: String(data[OVERRIDE_COMMENT_KEY] ?? ''),
      [ADHERENCE_COMMENT_KEY]: String(data[ADHERENCE_COMMENT_KEY] ?? ''),
    }
  } catch {
    return null
  }
}

export function getAdherenceCommentText(raw?: string | null, hasOverrideReason = false): string {
  const structured = parseContainerComments(raw)
  if (structured) {
    return structured[ADHERENCE_COMMENT_KEY].trim()
  }
  if (isBlankContainerComments(raw)) {
    return ''
  }
  // Legacy plain text is adherence only when there is no override reason.
  if (!hasOverrideReason) {
    return raw!.trim()
  }
  return ''
}

export function getOverrideCommentText(raw?: string | null): string {
  const structured = parseContainerComments(raw)
  if (structured) {
    return structured[OVERRIDE_COMMENT_KEY].trim()
  }
  if (isBlankContainerComments(raw)) {
    return ''
  }
  // Legacy plain text is treated as override when callers have a reason (ExamReason / Subjects).
  return raw!.trim()
}
